import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { Header } from '../components/layout/Header';
import { Navigation } from '../components/layout/Navigation';
import { MobileStickyBar } from '../components/layout/MobileStickyBar';
import { Footer } from '../components/layout/Footer';
import { useScreenInit } from '../useScreenInit';
import { useCategorySeo } from '../hooks/useCategorySeo';
import { CategoryData, Product } from '../data/residentialCategories';
import { ListingToolbar, ListingPagination, SortKey, PageSize, SORT_OPTIONS, PAGE_SIZE_OPTIONS } from '../components/category/ListingControls';
import { Phone, Package, ExternalLink, ChevronRight, Info } from 'lucide-react';
function parsePrice(price: string): number {
  const n = parseFloat((price || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}
function sortProducts(products: Product[], sort: SortKey): Product[] {
  const list = [...products];
  switch (sort) {
    case 'price-asc':
    case 'price-desc':
      {
        const dir = sort === 'price-asc' ? 1 : -1;
        return list.sort((a, b) => {
          const pa = parsePrice(a.price);
          const pb = parsePrice(b.price);
          const aMissing = Number.isNaN(pa);
          const bMissing = Number.isNaN(pb);
          // Priced items always sort before quote-only / missing-price items.
          if (aMissing && bMissing) return 0;
          if (aMissing) return 1;
          if (bMissing) return -1;
          return (pa - pb) * dir;
        });
      }
    case 'name-asc':
      return list.sort((a, b) => a.title.localeCompare(b.title));
    case 'name-desc':
      return list.sort((a, b) => b.title.localeCompare(a.title));
    case 'recommended':
    default:
      return list;
  }
}
function ProductCard({
  product
}: {
  product: Product;
}) {
  const [imageError, setImageError] = useState(false);
  return <div className="group bg-white rounded-xl border border-brand-border overflow-hidden hover:shadow-md hover:border-brand-maroon/50 transition-all flex flex-col h-full" data-id="element-2094">
      <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block relative bg-white aspect-square p-6 border-b border-brand-border/50 flex items-center justify-center" aria-label={`View ${product.title}`} data-id="element-2095">
        {!imageError ? <img src={product.imageUrl} alt={product.title} onError={() => setImageError(true)} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" data-id="element-2096" /> : <div className="w-full h-full flex flex-col items-center justify-center text-brand-charcoal/20" data-id="element-2097">
            <Package size={48} strokeWidth={1} data-id="element-2098" />
            <span className="mt-2 text-xs font-medium tracking-wide" data-id="element-2099">
              Image Unavailable
            </span>
          </div>}
      </a>

      <div className="p-5 flex flex-col flex-grow" data-id="element-2100">
        <div className="text-xs text-gray-500 mb-1 font-mono uppercase tracking-wider" data-id="element-2101">
          {product.sku}
        </div>
        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-charcoal hover:text-brand-maroon transition-colors mb-3 line-clamp-2 leading-snug" data-id="element-2102">
          {product.title}
        </a>
        <div className="text-lg font-bold text-brand-charcoal mb-5 mt-auto" data-id="element-2103">
          {product.price}
        </div>

        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block w-full bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal text-center py-2.5 rounded-md font-semibold transition-colors text-sm flex items-center justify-center gap-2" data-id="element-2104">
          View Details <ExternalLink size={14} data-id="element-2105" />
        </a>
      </div>
    </div>;
}
export function ResidentialCategory({
  category,
  context = 'Residential'
}: {
  category: CategoryData;
  context?: string;
}) {
  useScreenInit();
  const seo = useCategorySeo(category.route);
  const [searchParams, setSearchParams] = useSearchParams();
  const products = category.products;
  const hasProducts = products.length > 0;
  const sortParam = searchParams.get('sort') as SortKey | null;
  const sort: SortKey = sortParam && SORT_OPTIONS.some(o => o.value === sortParam) ? sortParam : 'recommended';
  const sizeParam = searchParams.get('pageSize');
  const pageSize: PageSize = sizeParam === 'all' ? 'all' : sizeParam && PAGE_SIZE_OPTIONS.some(o => String(o.value) === sizeParam) ? Number(sizeParam) as PageSize : 16;
  const sorted = useMemo(() => sortProducts(products, sort), [products, sort]);
  const total = sorted.length;
  const perPage = pageSize === 'all' ? total || 1 : pageSize;
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / perPage));
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(1, Number.isFinite(pageParam) ? pageParam : 1), totalPages);
  const startIdx = pageSize === 'all' ? 0 : (page - 1) * perPage;
  const visible = pageSize === 'all' ? sorted : sorted.slice(startIdx, startIdx + perPage);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = pageSize === 'all' ? total : Math.min(startIdx + perPage, total);
  const resultLabel = pageSize === 'all' ? `Showing all ${total} ${total === 1 ? 'product' : 'products'}` : `Showing ${showingFrom}-${showingTo} of ${total} products`;
  const paginationActive = pageSize !== 'all' && totalPages > 1;
  const updateParams = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => v === null ? sp.delete(k) : sp.set(k, v));
    setSearchParams(sp, {
      replace: true
    });
  };
  const onSortChange = (s: SortKey) => updateParams({
    sort: s === 'recommended' ? null : s,
    page: null
  });
  const onPageSizeChange = (p: PageSize) => updateParams({
    pageSize: p === 16 ? null : String(p),
    page: null
  });
  const onPageChange = (p: number) => updateParams({
    page: p <= 1 ? null : String(p)
  });
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-2106">
      <TopBar data-id="element-2107" />
      <Header data-id="element-2108" />
      <Navigation data-id="element-2109" />

      <main className="flex-grow" data-id="element-2110">
        {/* Category Hero */}
        <section className="bg-white border-b border-brand-border py-8 lg:py-10" data-id="element-2111">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2112">
            {/* Breadcrumbs */}
            <nav className="flex text-sm text-gray-500 mb-4 overflow-x-auto hide-scrollbar whitespace-nowrap pb-2" data-id="element-2113">
              <ol className="flex items-center space-x-2" data-id="element-2114">
                <li data-id="element-2115">
                  <a href="/Home.html" className="hover:text-brand-maroon transition-colors" data-id="element-2116">
                    Home
                  </a>
                </li>
                <li data-id="element-2117">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2118" />
                </li>
                <li data-id="element-2119">
                  <span className="text-gray-400" data-id="element-2120">{context}</span>
                </li>
                <li data-id="element-2121">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2122" />
                </li>
                <li className="text-brand-charcoal font-medium" data-id="element-2123">
                  {category.label}
                </li>
              </ol>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6" data-id="element-2124">
              <div className="max-w-3xl" data-id="element-2125">
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-serif font-bold text-brand-charcoal mb-4" data-id="element-2126">
                  {category.label}
                </h1>
                {seo?.description ? <p className="text-lg text-brand-muted leading-relaxed" data-id="element-2127">
                    {seo.description}
                  </p> : <div className="space-y-2.5 max-w-2xl" aria-hidden="true" role="presentation" data-id="element-2128">
                    <div className="h-4 rounded bg-brand-border/60 w-full" data-id="element-2129" />
                    <div className="h-4 rounded bg-brand-border/60 w-11/12" data-id="element-2130" />
                    <div className="h-4 rounded bg-brand-border/60 w-3/5" data-id="element-2131" />
                  </div>}
              </div>

              <div className="shrink-0 bg-brand-goldWash border border-brand-gold/30 rounded-xl p-5 flex items-center gap-4" data-id="element-2132">
                <div className="w-10 h-10 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm shrink-0" data-id="element-2133">
                  <Phone size={20} data-id="element-2134" />
                </div>
                <div data-id="element-2135">
                  <p className="text-sm font-bold text-brand-charcoal mb-0.5" data-id="element-2136">
                    Need help choosing?
                  </p>
                  <a href="tel:301-620-7585" className="text-brand-maroon font-semibold hover:underline" data-id="element-2137">
                    Call 301-620-7585
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="py-8 lg:py-10" data-id="element-2138">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2139">
            {hasProducts ? <>
                {/* Listing controls toolbar */}
                <div className="mb-6" data-id="element-2140">
                  <ListingToolbar sort={sort} onSortChange={onSortChange} pageSize={pageSize} onPageSizeChange={onPageSizeChange} resultLabel={resultLabel} data-id="element-2141" />
                </div>

                {paginationActive && <div className="mb-6" data-id="element-2142">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2143" />
                  </div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-2144">
                  {visible.map((product, idx) => <ProductCard key={`${product.sku}-${startIdx}-${idx}`} product={product} data-id="element-2145" />)}
                </div>

                {paginationActive && <div className="mt-8" data-id="element-2146">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2147" />
                  </div>}
              </> : <div className="bg-white border border-brand-border rounded-xl p-8 lg:p-12 text-center max-w-2xl mx-auto" data-id="element-2148">
                <div className="w-14 h-14 rounded-full bg-brand-goldWash border border-brand-gold/30 text-brand-maroon flex items-center justify-center mx-auto mb-5" data-id="element-2149">
                  <Package size={26} strokeWidth={1.5} data-id="element-2150" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-2" data-id="element-2151">
                  Browse {category.label} with our team
                </h2>
                <p className="text-brand-muted leading-relaxed mb-6" data-id="element-2152">
                  We carry a full selection in this category. Tell us what
                  you’re looking for and we’ll help you find the right option.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2153">
                  <a href="tel:301-620-7585" className="inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroonDark text-white px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2154">
                    <Phone size={16} data-id="element-2155" /> Call 301-620-7585
                  </a>
                  <a href="/ContactUs.html" className="inline-flex items-center justify-center gap-2 bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2156">
                    Ask for help
                  </a>
                </div>
              </div>}
          </div>
        </section>

        {/* Buyer guidance / SEO supporting copy */}
        {seo?.extraText && <section className="pb-12 lg:pb-16" data-id="element-2157">
            <div className="max-w-7xl mx-auto px-4" data-id="element-2158">
              <div className="bg-white border border-brand-border rounded-xl p-6 lg:p-8 flex flex-col sm:flex-row gap-5" data-id="element-2159">
                <div className="hidden sm:flex w-11 h-11 rounded-xl bg-brand-goldWash border border-brand-gold/30 text-brand-maroon items-center justify-center shrink-0" data-id="element-2160">
                  <Info size={22} strokeWidth={1.75} data-id="element-2161" />
                </div>
                <div className="max-w-3xl" data-id="element-2162">
                  <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-3" data-id="element-2163">
                    About {category.label}
                  </h2>
                  <p className="text-brand-muted leading-relaxed whitespace-pre-line" data-id="element-2164">
                    {seo.extraText}
                  </p>
                </div>
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-2165" />
      <MobileStickyBar data-id="element-2166" />
    </div>;
}